import { newRelicConstants } from '../constants';
import {
  bannerTypes,
  CLICK_CONVERSION_BEACON_TIME_TO_LIVE,
  VIEW_CONVERSION_BEACON_TIME_TO_LIVE,
  schemas
} from '../constants/constants';
import { htmlAttributes } from '../constants/htmlAttributes';
import { htmlSelectors } from '../constants/htmlSelectors';

/**
 * For homepage banners, this function uses THDCustomer object to determine
 * if the schema should be guest or authenticated
 */
const getHomepageSchema = () => {
  if (typeof window !== 'undefined') {
    const isLoggedIn = window?.THDCustomer?.default?.isLoggedIn || false;
    return (isLoggedIn ? schemas.HP_SPONSORED_AUTH : schemas.HP_SPONSORED);
  }
  return schemas.HP_SPONSORED;
};

/**
 * This function validates a given schema or determines the appropriate schema
 * based on pageContext.
 */
export const getSchema = ({ schema = '', pageContext = {}, props }) => {

  const { label, isCategory, isSearch, isBrowse } = pageContext;
  // schema props is from educational layout
  if (props?.schema) return props.schema;
  if (schema) {
    // if schema is homepage, determine authenticated or guest
    if (schema.includes('hp')) {
      return getHomepageSchema();
    }
    return schema;
  }

  if (label === 'browse-search') {
    if (isCategory) {
      return 'cat_sponsored';
    }
    if (isSearch) {
      return 'search_sponsored';
    }
    if (isBrowse) {
      return 'browse_sponsored';
    }
  }

  return null;
};

export const getSchemaId = (schema, bannerType) => {
  return `${schema}__${bannerType}`;
};

/**
 * Extracts the creative ID from a given banner URL.
 *
 * The function looks for a specific pattern in the URL, where the creative ID
 * is expected to be in the format `-eg<number>-`. If the pattern is found,
 * it returns the creative ID in the format `eg<number>`. If the pattern is
 * not found or the input is invalid, it returns an empty string.
 *
 * @param {string} bannerURL - The URL of the banner to extract the creative ID from.
 * @returns {string} The extracted creative ID in the format `eg<number>`, or an empty string if not found.
 */
export const getCreativeId = (bannerURL) => {
  if (!bannerURL) return '';
  // Parse the URL
  const match = bannerURL.match(/-eg(\d+)-/);
  const egValue = match ? `eg${match[1]}` : null;
  return egValue;
};

/**
 * Converts the first letter of a given string to uppercase.
 *
 * @param {string} string - The input string to be transformed.
 * @returns {string} The transformed string with the first letter in uppercase.
 * If the input is falsy, an empty string is returned.
 */
export const toUpperCaseFirstLetter = (string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const shouldSuppressBanner = ({ experienceContext, pageContext, suppressedNValues }) => {
  const { path } = experienceContext;
  const { data } = pageContext;

  // Suppression of banners based of n-values in config service
  const currentNValue = path?.match(/N-[^/?]+/)?.[0];
  const nValueArray = suppressedNValues ? String(suppressedNValues)?.split(',').map((nValue) => nValue.trim()) : [];

  if ((currentNValue && nValueArray.includes(currentNValue))) {
    return true;
  }

  const hasBrandCrumb = data?.searchModel?.taxonomy?.breadCrumbs?.some((crumb) => crumb.dimensionName === 'Brand');
  const isFeaturedProductNode = path?.indexOf('/b/Featured-Products') !== -1;

  const hasBrandSearch = data?.searchModel?.metadata?.analytics?.semanticTokens
    && data.searchModel.metadata.analytics.semanticTokens.indexOf('br:{') !== -1;

  return hasBrandCrumb || hasBrandSearch || isFeaturedProductNode;
};

const getSkusFromAdditionalProductsIds = (searchModel) => {
  const additionalProductSkus = searchModel?.additionalProductsIds?.productIds?.map((product) => product.itemId);
  const productSkus = searchModel?.products?.map((product) => product.itemId);
  const skus = additionalProductSkus.concat(productSkus);
  const uniqueSkus = [...new Set(skus)];
  return uniqueSkus.reduce((total, sku) => total + ',' + sku, '').slice(1) || '';
};

const getSkusFromSearchModel = (products) => {
  return products
    .map((product) => product.itemId)
    .reduce((total, sku) => total + ',' + sku, '').slice(1) || '';
};

const getSkusFromLocalStorage = () => {
  const retailMediaRecentlyViewedSku = localStorage.getItem('retailMediaRecentlyViewedSku');
  const retailMediaRecentlyViewedSkuRefreshAt = localStorage.getItem('retailMediaRecentlyViewedSkuRefreshAt');
  // eslint-disable-next-line
  if (retailMediaRecentlyViewedSku && retailMediaRecentlyViewedSkuRefreshAt && parseInt(retailMediaRecentlyViewedSkuRefreshAt) >= parseInt(new Date().getTime())) {
    return retailMediaRecentlyViewedSku;
  }
  return '';
};

export const getSkus = ({ data }, utilizeRvData) => {
  if (data?.searchModel?.additionalProductsIds) {
    return getSkusFromAdditionalProductsIds(data?.searchModel);
  }
  if (data?.searchModel?.products) {
    return getSkusFromSearchModel(data?.searchModel?.products);
  }
  if (utilizeRvData) {
    return getSkusFromLocalStorage();
  }
  return '';
};

export const getUrlParams = (url) => {
  const params = {};

  try {
    url.split('?')[1].split('&').forEach((el) => {
      const keyVal = el.split('=');
      params[keyVal[0]] = keyVal[1];
    });
  } catch (_error) { /* */ }

  return params;
};

/* This function is to check whether a new data model will be
required for a given experience. Experiences that already use Contentful
(my-homepage, landing-page) already have dataModels set up and skipping
the component level dataModel keeps them from conflicting */
export const shouldSkipDataModel = (schema) => {
  if (schema !== '' && schema !== 'localad_sponsored') {
    return true;
  }
  return false;
};

export const getDefaultBanner = ({ channel, isConsumerApp }, contentfulDefaultBanner) => {

  const defaultBannerMap = [
    {
      imgDesktop: 'https://contentgrid.thdstatic.com/hdus/en_US/DTCCOMNEW/fetch/bopis-desktop.jpg',
      imgMobile: 'https://contentgrid.thdstatic.com/hdus/en_US/DTCCOMNEW/fetch/bopis-mobile.jpg',
      clickthruURL: 'https://www.homedepot.com/c/pick_up_in_store',
    },
    {
      imgDesktop: 'https://contentgrid.thdstatic.com/hdus/en_US/DTCCOMNEW/fetch/delivery-desktop.jpg',
      imgMobile: 'https://contentgrid.thdstatic.com/hdus/en_US/DTCCOMNEW/fetch/delivery-mobile.jpg',
      clickthruURL: 'https://www.homedepot.com/c/About_Your_Online_Order',
    },
    {
      imgDesktop: 'https://contentgrid.thdstatic.com/hdus/en_US/DTCCOMNEW/fetch/homeservices-desktop1.jpg',
      imgMobile: 'https://contentgrid.thdstatic.com/hdus/en_US/DTCCOMNEW/fetch/homeservices-mobile1.jpg',
      clickthruURL: 'https://www.homedepot.com/services/?vfrom=capability',
    },
  ];
  if (!isConsumerApp) {
    defaultBannerMap.push({
      imgDesktop: 'https://contentgrid.thdstatic.com/hdus/en_US/DTCCOMNEW/fetch/app-desktop.jpg',
      imgMobile: 'https://contentgrid.thdstatic.com/hdus/en_US/DTCCOMNEW/fetch/app-mobile.jpg',
      clickthruURL: 'https://www.homedepot.com/c/SF_Mobile_Shopping'
    });
  }

  const useContentfulEventBanner = contentfulDefaultBanner?.specialDefaultBanner?.useSpecialBanner;
  const useContentfulPageBanner = contentfulDefaultBanner?.specialDefaultBanner?.useSpecialBanner === false
    && contentfulDefaultBanner.clickthruUrl !== undefined && contentfulDefaultBanner.clickthruUrl !== null;

  let defaultBanner = {};
  if (useContentfulEventBanner) {
    defaultBanner = contentfulDefaultBanner.specialDefaultBanner;
  } else if (useContentfulPageBanner) {
    defaultBanner = contentfulDefaultBanner;
  } else {
    defaultBanner = defaultBannerMap[Math.floor(Math.random() * defaultBannerMap.length)];
  }

  // Making the defaultBanner object editable again
  defaultBanner = { ...defaultBanner };

  defaultBanner.url = defaultBanner.imgDesktop;
  if (channel === 'mobile') {
    defaultBanner.url = defaultBanner.imgMobile;
  }

  let imgSrc = defaultBanner?.url;
  let href = defaultBanner.clickthruURL || defaultBanner.clickthruUrl;
  if (typeof window !== 'undefined') {
    try {
      const aTag = window.document.querySelector('#default_banner_carousel a');
      const img = window.document.querySelector('#default_banner_carousel img');
      href = aTag.href;
      imgSrc = img.src;
    } catch (_error) { /* */ }
  }

  const paddingTop = channel === 'mobile' ? '37.7%' : '10.6%';

  const spacingCss = {
    width: '100%',
    display: 'block',
    boxSizing: 'border-box',
    position: 'relative',
    paddingTop
  };

  const bgCss = {
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100%',
    backgroundImage: `url("${imgSrc}")`
  };

  return { spacingCss, bgCss, href, imgSrc };
};

export const isServer = () => {
  return typeof window === 'undefined';
};

export const isEventBusCreated = () => {
  return typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS;
};

export const adIsNonEndemic = (customRenderId) => {
  if (customRenderId === 'sponsoredmiddlebanner') {
    return true;
  }
  return false;
};

export const adIsCategoryOrBrowse = (schema) => {
  const adType = schema.toLowerCase();
  if (adType === 'cat_sponsored' || adType === 'browse_sponsored') {
    return true;
  }
  return false;
};

export const nValueIsValid = (nValue) => {
  if (nValue?.startsWith('N-') && nValue?.includes('Z')) {
    return true;
  }
  return false;
};

export const isNValueRequiredAndValid = (slugId, customRenderId, schema) => {
  const nValue = slugId;

  if (!adIsNonEndemic(customRenderId) && adIsCategoryOrBrowse(schema) && !nValueIsValid(nValue)) {
    return false;
  }
  return true;
};

export const triggerNewRelic = (adType, eventType) => {
  if (!window.sponsoredContent) {
    window.sponsoredContent = {};
  }

  if (window.newrelic && typeof LIFE_CYCLE_EVENT_BUS !== 'undefined') {
    window.LIFE_CYCLE_EVENT_BUS.trigger('sponsored-content.event', { adType, event: eventType });
    window.sponsoredContent[adType] = true;
  }
};

export function getIframeAnchorTags(bannerType) {
  let anchorTags = [];
  const targetIframe = [...document.querySelectorAll('iframe[data-revjet-options]')]
    .find((el) => el.attributes['data-revjet-options'].value.includes(bannerType));

  if (targetIframe) {
    anchorTags = [...targetIframe?.contentWindow.document.querySelectorAll('a')];
  }

  return anchorTags;
}

function persistConversionBeacon(mode, conversionBeaconData) {
  const timeToLive = mode === 'click' ? CLICK_CONVERSION_BEACON_TIME_TO_LIVE : VIEW_CONVERSION_BEACON_TIME_TO_LIVE;
  const keyName = mode === 'click' ? 'clickConversionBeacon' : 'viewConversionBeacon';

  if (conversionBeaconData?.conversionBeacon) {
    const { conversionBeacon, mcvisID } = conversionBeaconData;
    const exp = Date.now() + timeToLive;

    const lsItem = {
      url: conversionBeacon,
      mcvisID,
      exp,
    };

    localStorage.setItem(keyName, JSON.stringify(lsItem));
  }
}

export function persistViewConversionBeacon(conversionBeaconData) {
  persistConversionBeacon('view', conversionBeaconData);
}
export function persistClickConversionBeacon(conversionBeaconData) {
  persistConversionBeacon('click', conversionBeaconData);
}

export function getBannerHtmlSelector(bannerType) {
  if (bannerType === bannerTypes.SKYSCRAPER) {
    return htmlSelectors.NUCLEUS_SKYSCRAPER_BANNER;
  }
  if (bannerType === bannerTypes.HERO) {
    return htmlSelectors.NUCLEUS_HERO_BANNER;
  }
  if (bannerType === bannerTypes.MIDDLE) {
    return htmlSelectors.NUCLEUS_PIP_BANNER;
  }
  if (bannerType === 'sponsored__banner' || bannerType === 'auth__banner') {
    return htmlSelectors.NUCLEUS_STANDARD_BANNER;
  }
  if (bannerType === bannerTypes.STANDARD) {
    return htmlSelectors.NUCLEUS_SPONSORED_STANDARD_BANNER;
  }
  if (bannerType === bannerTypes.INGRID) {
    return htmlSelectors.NUCLEUS_INGRID_BANNER;
  }

  if (bannerType === bannerTypes.LEADERBOARD) {
    return htmlSelectors.NUCLEUS_LEADERBOARD_BANNER;
  }
  return null;
}

export const getClickEventType = (clickEvent) => {
  if (clickEvent === 1) {
    return newRelicConstants.CLICK_MIDDLE;
  }
  if (clickEvent === 2) {
    return newRelicConstants.CLICK_RIGHT;
  }
  return newRelicConstants.CLICK_LEFT;
};

export const getNewRelicConstant = (bannerType) => {
  if (bannerType === bannerTypes.HERO) {
    return newRelicConstants.HERO_BANNER;
  }
  if (bannerType === bannerTypes.SKYSCRAPER) {
    return newRelicConstants.SKYSCRAPER_BANNER;
  }
  if (bannerType === bannerTypes.MIDDLE) {
    return newRelicConstants.PIP_BANNER;
  }
  if (bannerType === bannerTypes.STANDARD || bannerType === 'sponsored__banner' || bannerType === 'auth__banner') {
    return newRelicConstants.STANDARD_BANNER;
  }
  if (bannerType === bannerTypes.INGRID) {
    return newRelicConstants.INGRID_BANNER;
  }
  return null;
};

export function onClickHandler(bannerType, anchorTags, clickConversionBeaconData) {
  const bannerSelector = getBannerHtmlSelector(bannerType);
  const bannerAttributes = document.querySelector(bannerSelector);
  const adServer = bannerAttributes.getAttribute(htmlAttributes.DATA_ADSERVER);
  const slotId = bannerAttributes.getAttribute(htmlAttributes.DATA_SLOT_ID);

  triggerNewRelic(getNewRelicConstant(bannerType), `${newRelicConstants.CLICK}-${adServer}-${slotId}`);
  persistClickConversionBeacon(clickConversionBeaconData);
  if (anchorTags.length === 1) {
    try {
      window.removeEventListener('message');
    } catch (_error) {
      triggerNewRelic(getNewRelicConstant(bannerType), 'error-removing-click-listener');
    }

  } else if (anchorTags.length > 1) {
    anchorTags.forEach((link) => {
      link.removeEventListener('click', onClickHandler);
    });
  }
}

export const triggerNewRelicTimeLog = (componentName, startTime, endTime, adServer = '') => {
  if (!window.sponsoredContent) {
    window.sponsoredContent = {};
  }

  if (window.newrelic && typeof LIFE_CYCLE_EVENT_BUS !== 'undefined') {
    window.LIFE_CYCLE_EVENT_BUS.trigger('performance.component-load', {
      componentName: `${componentName}${adServer ? `-${adServer.toLowerCase()}` : ''}`,
      startTime,
      endTime,
    });

    window.sponsoredContent[componentName] = true;
  }
};

export function parseInnervateEventData(event) {
  try {
    const data = JSON.parse(event.data);
    if (data.module === 'REVJET_Creative' && data.method === 'dispatchEvent') {
      return data?.properties?.eventObject;
    }
    return null;
  } catch {
    return null;
  }
}

export function onClickMetricsHandler(bannerType, anchorTags, clickConversionBeaconData, mode) {
  if (anchorTags.length === 1) {
    window.addEventListener('message', (event) => {
      // Ensure the event is from the same origin to prevent cross-site scripting attacks
      if (event.origin !== window.location.origin) {
        return;
      }
      const eventObject = parseInnervateEventData(event);
      if (eventObject && eventObject.type === 'click') {
        onClickHandler(bannerType, anchorTags, clickConversionBeaconData);
      }
    }, false);
  } else if (anchorTags.length > 1) {
    anchorTags.forEach((link) => {
      if (mode === 'test') {
        link.addEventListener('click', () => this.newRelicClickHandler(bannerType, anchorTags), true);
      } else {
        link.addEventListener('click', () => {
          onClickHandler(bannerType, anchorTags, clickConversionBeaconData);
        }, true);
      }
    });
  }

}

export function parseIfJson(str) {
  try {
    return JSON.parse(str);
  } catch (_error) {
    return {};
  }
}

export const onTriggerNewRelicAdBlocked = (adType) => {
  if (!adType) return;
  triggerNewRelic(adType, newRelicConstants.AD_BLOCKED);
};

export const hasAnchorSku = (pageContext) => {
  return pageContext?.data?.itemId;
};
